import React, { useMemo, useState } from 'react';
import {
  ActionList,
  ActionListItem,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerHeader,
  Dropdown,
  DropdownOverlay,
  Heading,
  SearchInput,
  SelectInput,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TableRow,
  TableToolbar,
  TableToolbarActions,
  Text,
} from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import api from 'api';
import {
  FilingDataListItem,
  FilingDataListResponse,
  FilingStatus,
  FilingType,
  Quarter,
  TDS_FILING_DATA_QUERY_KEYS,
} from 'api/tdsFilingData';
import { TdsFilingDetails } from './TdsFilingDetails';
import {
  STATUS_BADGE_COLOR,
  STATUS_LABEL,
  PROCESSING_STATUSES,
  formatEpochDateTime,
  getDefaultQuarterAndFY,
} from './utils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface FilingGroup {
  key: string;
  organization_id: number | null;
  merchant_id: string | null;
  filing_type: FilingType;
  quarter: Quarter;
  fy: string;
  latestFiling: FilingDataListItem;
  versions: FilingDataListItem[]; // sorted desc by version
}

// ── Constants ─────────────────────────────────────────────────────────────────

const QUARTER_OPTIONS: Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];
const FILING_TYPE_OPTIONS: FilingType[] = ['24Q', '26Q', '138', '140'];
const STATUS_OPTIONS: FilingStatus[] = [
  'requested',
  'generation_completed',
  'generation_failed',
  'processing',
  'completed',
  'failed',
];
const currentYear = new Date().getFullYear();
const FY_OPTIONS = Array.from({ length: 4 }, (_, i) => {
  const start = currentYear - i;
  return `${start}-${String(start + 1).slice(-2)}`;
});

const PROCESSING_STATUS_VALUES = new Set<FilingStatus>(PROCESSING_STATUSES);

// ── Export to CSV helper ──────────────────────────────────────────────────────

function exportToCsv(groups: FilingGroup[], quarter?: string, fy?: string) {
  const headers = ['Org ID', 'Type', 'Quarter', 'FY', 'Latest Status', 'Updated At', 'Errors', 'Versions'];
  const rows = groups.map((g) => {
    const l = g.latestFiling;
    const displayStatus = l.display_status ?? l.status;
    const versions = g.versions.length > 1
      ? `v${l.version} of ${g.versions.length}`
      : `v${l.version}`;
    return [
      l.organization_id ?? l.merchant_id ?? '',
      l.filing_type,
      l.quarter,
      l.fy,
      STATUS_LABEL[displayStatus] ?? displayStatus,
      formatEpochDateTime(l.status_updated_at),
      l.error_count,
      versions,
    ];
  });

  const csv = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const ts   = new Date().toISOString().slice(0, 10);
  link.href     = url;
  link.download = `tds-filing${quarter ? `-${quarter}` : ''}${fy ? `-${fy}` : ''}-${ts}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ── Status summary bar ────────────────────────────────────────────────────────

const SUMMARY_STATUSES: { key: FilingStatus; label: string; color: string }[] = [
  { key: 'completed',                          label: 'Completed',          color: '#15803d' },
  { key: 'tds_filing_completed',               label: 'Filed',              color: '#15803d' },
  { key: 'filing_data_generation_triggered',   label: 'Triggered',          color: '#6b7280' },
  { key: 'filing_data_generation_started',     label: 'Started',            color: '#6b7280' },
  { key: 'processing',                         label: 'Parsing',            color: '#b45309' },
  { key: 'fvu_generation_triggered',           label: 'FVU Triggered',      color: '#1d4ed8' },
  { key: 'fvu_generation_completed',           label: 'FVU Done',           color: '#1d4ed8' },
  { key: 'tds_filing_triggered',               label: 'Filing Triggered',   color: '#1d4ed8' },
  { key: 'failed',                             label: 'Parse Failed',       color: '#dc2626' },
  { key: 'generation_failed',                  label: 'Gen Failed',         color: '#ea580c' },
  { key: 'filing_data_generation_failed',      label: 'Job Failed',         color: '#dc2626' },
  { key: 'fvu_generation_failed',              label: 'FVU Failed',         color: '#dc2626' },
  { key: 'tds_filing_failed',                  label: 'Filing Failed',      color: '#dc2626' },
  { key: 'filing_data_generation_skipped',     label: 'Skipped',            color: '#6b7280' },
];

interface StatusSummaryBarProps {
  counts: Partial<Record<FilingStatus, number>>;
  total: number;
}

const StatusSummaryBar = ({ counts, total }: StatusSummaryBarProps) => {
  const nonZero = SUMMARY_STATUSES.filter((s) => (counts[s.key] ?? 0) > 0);
  if (nonZero.length === 0) return null;

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gap="spacing.3"
      marginBottom="spacing.4"
      padding="spacing.4"
      backgroundColor="surface.background.gray.subtle"
      borderRadius="medium"
      alignItems="center"
    >
      <Text size="small" weight="semibold" color="surface.text.gray.muted">
        {total} total
      </Text>
      {nonZero.map(({ key, label }) => (
        <Box key={key} display="flex" alignItems="center" gap="spacing.2">
          <Badge color={STATUS_BADGE_COLOR[key]}>
            {label}: {counts[key] ?? 0}
          </Badge>
        </Box>
      ))}
    </Box>
  );
};

// ── Grouping helper ───────────────────────────────────────────────────────────

function groupFilings(filings: FilingDataListItem[]): FilingGroup[] {
  const map = new Map<string, FilingDataListItem[]>();

  for (const f of filings) {
    const entityId = f.organization_id ?? f.merchant_id ?? 'unknown';
    const key = `${entityId}|${f.filing_type}|${f.quarter}|${f.fy}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(f);
  }

  const groups: FilingGroup[] = [];
  for (const [key, versions] of map.entries()) {
    const sorted = [...versions].sort((a, b) => b.version - a.version);
    const latest = sorted[0];
    groups.push({
      key,
      organization_id: latest.organization_id,
      merchant_id: latest.merchant_id,
      filing_type: latest.filing_type,
      quarter: latest.quarter,
      fy: latest.fy,
      latestFiling: latest,
      versions: sorted,
    });
  }

  // Sort groups: processing first, then by latest updated_at desc
  return groups.sort((a, b) => b.latestFiling.status_updated_at - a.latestFiling.status_updated_at);
}

// ── Version History Drawer ────────────────────────────────────────────────────

interface VersionDrawerProps {
  group: FilingGroup | null;
  onClose: () => void;
  onViewDetails: (filingId: string) => void;
}

const VersionHistoryDrawer = ({ group, onClose, onViewDetails }: VersionDrawerProps) => {
  const entityLabel = group?.organization_id ?? group?.merchant_id ?? '—';

  const handleDownloadTxt = async (filingId: string) => {
    const result = await api.tdsFilingData.getDownloadUrl(filingId, 'txt');
    window.open(result.signed_url, '_blank');
  };

  return (
    <Drawer isOpen={!!group} onDismiss={onClose} accessibilityLabel="Version history">
      <DrawerHeader
        title={`Version History`}
        subtitle={
          group
            ? `${group.filing_type} / ${group.quarter} / ${group.fy} — Org ${entityLabel}`
            : ''
        }
      />
      <DrawerBody>
        {group && (
          <Box>
            <Text size="small" color="surface.text.gray.muted" marginBottom="spacing.4">
              {group.versions.length} version{group.versions.length !== 1 ? 's' : ''} found.
              The latest version reflects the current filing status.
            </Text>
            <Divider marginBottom="spacing.4" />

            {/* Version rows */}
            {group.versions.map((v, idx) => {
              const isLatest = idx === 0;
              const isCompleted = v.status === 'completed';
              const isProcessing = PROCESSING_STATUS_VALUES.has(v.status);

              return (
                <Box
                  key={v.filing_data_id}
                  padding="spacing.4"
                  marginBottom="spacing.3"
                  borderRadius="medium"
                  backgroundColor={
                    isLatest
                      ? 'surface.background.primary.subtle'
                      : 'surface.background.gray.subtle'
                  }
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    {/* Left: version + status */}
                    <Box display="flex" alignItems="center" gap="spacing.4">
                      <Box>
                        <Text size="large" weight="semibold">
                          v{v.version}
                          {isLatest && (
                            <Text
                              as="span"
                              size="small"
                              color="surface.text.primary.normal"
                              weight="semibold"
                            >
                              {' '}(latest)
                            </Text>
                          )}
                        </Text>
                        <Text size="small" color="surface.text.gray.muted">
                          {formatEpochDateTime(v.status_updated_at)}
                        </Text>
                      </Box>

                      <Box display="flex" alignItems="center" gap="spacing.2">
                        <Badge color={STATUS_BADGE_COLOR[v.status]}>
                          {STATUS_LABEL[v.status]}
                        </Badge>
                        {isProcessing && (
                          <Spinner size="medium" accessibilityLabel="Processing" />
                        )}
                      </Box>

                      {v.error_count > 0 && (
                        <Badge color="negative">{v.error_count} error{v.error_count !== 1 ? 's' : ''}</Badge>
                      )}
                    </Box>

                    {/* Right: actions */}
                    <Box display="flex" flexDirection="column" gap="spacing.2" alignItems="flex-end">
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => onViewDetails(v.filing_data_id)}
                      >
                        View Details
                      </Button>
                      {isCompleted && (
                        <Button
                          size="small"
                          variant="tertiary"
                          onClick={() => handleDownloadTxt(v.filing_data_id)}
                        >
                          Download TXT
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DrawerBody>
    </Drawer>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

export const TdsFilingDataList = () => {
  const { quarter: defaultQuarter, fy: defaultFy } = getDefaultQuarterAndFY();

  const [page, setPage] = useState(1);
  const [orgIdSearch, setOrgIdSearch] = useState('');
  const [quarter, setQuarter] = useState<Quarter | undefined>(defaultQuarter as Quarter);
  const [fy, setFy] = useState<string | undefined>(defaultFy);
  const [filingType, setFilingType] = useState<FilingType | undefined>();
  const [statuses, setStatuses] = useState<FilingStatus[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<FilingGroup | null>(null);
  const [selectedFilingId, setSelectedFilingId] = useState<string | null>(null);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  // Track which group the details were opened from (for back navigation)
  const [detailsSourceGroup, setDetailsSourceGroup] = useState<FilingGroup | null>(null);
  const pageSize = 50;

  const queryParams = {
    org_ids: orgIdSearch || undefined,
    quarter,
    fy,
    filing_type: filingType,
    statuses: statuses.length > 0 ? statuses.join(',') : undefined,
    page,
    page_size: pageSize,
  };

  const { data, isLoading, isError, refetch } = useQuery<FilingDataListResponse>({
    queryKey: [TDS_FILING_DATA_QUERY_KEYS.list, queryParams],
    queryFn: () => api.tdsFilingData.listFilingData(queryParams),
    refetchInterval: (data) => {
      const hasProcessing = data?.items.some((r) =>
        PROCESSING_STATUS_VALUES.has((r.display_status ?? r.status) as FilingStatus),
      );
      return hasProcessing ? 10_000 : false;
    },
  });

  const allFilings = data?.items ?? [];

  // Separate query for status counts — covers ALL records for the filter, not just the current page.
  const countsParams = { org_ids: orgIdSearch || undefined, filing_type: filingType, quarter, fy };
  const { data: countsData } = useQuery({
    queryKey: [TDS_FILING_DATA_QUERY_KEYS.statusCounts, countsParams],
    queryFn: () => api.tdsFilingData.getStatusCounts(countsParams),
    enabled: !!(quarter || fy || filingType || orgIdSearch),
  });
  const statusCounts = countsData?.status_counts ?? {};
  const statusTotal  = countsData?.total ?? (data?.total_count ?? 0);

  // Group by org+type+quarter+fy — one row per unique combination
  const groups = useMemo(() => groupFilings(allFilings), [allFilings]);

  // For pagination display: number of unique org-period combinations
  const groupCount = groups.length;

  const handleViewVersions = (group: FilingGroup) => {
    if (group.versions.length === 1) {
      // Only one version — open details directly
      setSelectedFilingId(group.latestFiling.filing_data_id);
      setSelectedRequestId(group.latestFiling.request_id ?? null);
    } else {
      setSelectedGroup(group);
    }
  };

  const handleViewDetails = (filingId: string, fromGroup?: FilingGroup) => {
    setDetailsSourceGroup(fromGroup ?? null);
    setSelectedGroup(null);
    setSelectedFilingId(filingId);
    // Find request_id from the group's version matching this filingId
    const version = fromGroup?.versions.find((v) => v.filing_data_id === filingId);
    setSelectedRequestId(version?.request_id ?? fromGroup?.latestFiling.request_id ?? null);
  };

  // Table nodes — one per group, keyed by group key
  const tableNodes = groups.map((g) => ({
    ...g.latestFiling,
    id: g.key,
    _group: g,
  }));

  return (
    <Box>
      {/* Filters */}
      <Box display="flex" flexWrap="wrap" gap="spacing.4" marginBottom="spacing.5" alignItems="flex-end">
        <SearchInput
          label="Org ID"
          placeholder="Search by org ID"
          value={orgIdSearch}
          onChange={({ value }) => { setOrgIdSearch(value ?? ''); setPage(1); }}
          onClearButtonClick={() => { setOrgIdSearch(''); setPage(1); }}
        />

        <Dropdown>
          <SelectInput
            label="Filing Type"
            placeholder="All"
            value={filingType}
            onChange={({ values }) => { setFilingType(values[0] as FilingType | undefined); setPage(1); }}
          />
          <DropdownOverlay>
            <ActionList>
              <ActionListItem title="All" value="" />
              {FILING_TYPE_OPTIONS.map((t) => <ActionListItem key={t} title={t} value={t} />)}
            </ActionList>
          </DropdownOverlay>
        </Dropdown>

        <Dropdown>
          <SelectInput
            label="Quarter"
            placeholder="All"
            value={quarter}
            onChange={({ values }) => { setQuarter(values[0] as Quarter | undefined); setPage(1); }}
          />
          <DropdownOverlay>
            <ActionList>
              <ActionListItem title="All" value="" />
              {QUARTER_OPTIONS.map((q) => <ActionListItem key={q} title={q} value={q} />)}
            </ActionList>
          </DropdownOverlay>
        </Dropdown>

        <Dropdown>
          <SelectInput
            label="FY"
            placeholder="All"
            value={fy}
            onChange={({ values }) => { setFy(values[0] || undefined); setPage(1); }}
          />
          <DropdownOverlay>
            <ActionList>
              <ActionListItem title="All" value="" />
              {FY_OPTIONS.map((f) => <ActionListItem key={f} title={f} value={f} />)}
            </ActionList>
          </DropdownOverlay>
        </Dropdown>

        <Dropdown selectionType="multiple">
          <SelectInput
            label="Status"
            placeholder="All statuses"
            value={statuses}
            onChange={({ values }) => { setStatuses(values as FilingStatus[]); setPage(1); }}
          />
          <DropdownOverlay>
            <ActionList>
              {STATUS_OPTIONS.map((s) => <ActionListItem key={s} title={STATUS_LABEL[s]} value={s} />)}
            </ActionList>
          </DropdownOverlay>
        </Dropdown>

        <Box marginLeft="auto" display="flex" gap="spacing.3">
          <Button
            variant="secondary"
            onClick={() => exportToCsv(groups, quarter, fy)}
            isDisabled={groups.length === 0}
          >
            Export to CSV
          </Button>
          <Button onClick={() => { window.location.href = '/admin/fileTds'; }}>Generate Filing</Button>
        </Box>
      </Box>

      {isError && (
        <Box marginBottom="spacing.4">
          <Alert
            color="negative"
            description="Failed to load filing data."
            actions={{ primary: { text: 'Retry', onClick: () => refetch() } }}
          />
        </Box>
      )}

      {/* Status summary bar — counts reflect ALL records for the active filter */}
      {countsData && (
        <StatusSummaryBar counts={statusCounts} total={statusTotal} />
      )}

      {/* Main grouped table */}
      <Table
        data={{ nodes: tableNodes }}
        isLoading={isLoading}
        toolbar={
          <TableToolbar title={`${groupCount} filing${groupCount !== 1 ? 's' : ''}`}>
            <TableToolbarActions>
              <Button variant="tertiary" size="small" onClick={() => refetch()}>Refresh</Button>
            </TableToolbarActions>
          </TableToolbar>
        }
        pagination={
          <TablePagination
            currentPage={page - 1}
            defaultPageSize={50}
            paginationType="server"
            totalItemCount={data?.total_count ?? 0}
            onPageChange={({ page: p }) => setPage(p + 1)}
          />
        }
      >
        {(nodes) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>Org ID</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Quarter</TableHeaderCell>
                <TableHeaderCell>FY</TableHeaderCell>
                <TableHeaderCell>Latest Status</TableHeaderCell>
                <TableHeaderCell>Updated At</TableHeaderCell>
                <TableHeaderCell>Errors</TableHeaderCell>
                <TableHeaderCell>Versions</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {nodes.length === 0 && !isLoading && (
                <TableRow item={{ id: '__empty__' } as never}>
                  <TableCell>
                    <Box paddingY="spacing.8" display="flex" justifyContent="center" alignItems="center">
                      <Text color="surface.text.gray.muted">
                        No filings found
                        {orgIdSearch ? ` for org ID "${orgIdSearch}"` : ''}
                        {quarter ? ` in ${quarter}` : ''}
                        {fy ? ` FY ${fy}` : ''}
                        . Try adjusting your filters.
                      </Text>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {nodes.map((node) => {
                const group = (node as typeof node & { _group: FilingGroup })._group;
                const latest = group.latestFiling;
                const displayStatus = latest.display_status ?? latest.status;
                const isProcessing = PROCESSING_STATUS_VALUES.has(displayStatus);
                const isCompleted = displayStatus === 'completed' || displayStatus === 'tds_filing_completed';
                const hasMultipleVersions = group.versions.length > 1;

                return (
                  <TableRow key={group.key} item={node}>
                    <TableCell>
                      <Text weight="semibold">
                        {latest.organization_id ?? latest.merchant_id ?? '—'}
                      </Text>
                    </TableCell>
                    <TableCell><Text>{latest.filing_type}</Text></TableCell>
                    <TableCell><Text>{latest.quarter}</Text></TableCell>
                    <TableCell><Text>{latest.fy}</Text></TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap="spacing.2">
                        <Badge color={STATUS_BADGE_COLOR[displayStatus]}>
                          {STATUS_LABEL[displayStatus]}
                        </Badge>
                        {isProcessing && (
                          <Spinner size="medium" accessibilityLabel="Processing" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Text>{formatEpochDateTime(latest.status_updated_at)}</Text>
                    </TableCell>
                    <TableCell>
                      {latest.error_count > 0 ? (
                        <Badge color="negative">
                          {latest.error_count} error{latest.error_count !== 1 ? 's' : ''}
                        </Badge>
                      ) : (
                        <Text color="surface.text.gray.muted">—</Text>
                      )}
                    </TableCell>
                    <TableCell>
                      <Text>
                        {hasMultipleVersions
                          ? `v${latest.version} of ${group.versions.length}`
                          : `v${latest.version}`}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap="spacing.2">
                        <Button
                          size="xsmall"
                          variant="secondary"
                          onClick={() => handleViewVersions(group)}
                        >
                          {hasMultipleVersions ? 'View Versions' : 'View'}
                        </Button>
                        {isCompleted && !hasMultipleVersions && (
                          <Button
                            size="xsmall"
                            variant="tertiary"
                            onClick={async () => {
                              const result = await api.tdsFilingData.getDownloadUrl(
                                latest.filing_data_id,
                              );
                              window.open(result.signed_url, '_blank');
                            }}
                          >
                            Download
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </>
        )}
      </Table>


      {/* Version history drawer */}
      <VersionHistoryDrawer
        group={selectedGroup}
        onClose={() => setSelectedGroup(null)}
        onViewDetails={(filingId) => handleViewDetails(filingId, selectedGroup ?? undefined)}
      />

      {/* Filing details drawer */}
      <TdsFilingDetails
        filingId={selectedFilingId}
        requestId={selectedRequestId}
        onClose={() => {
          setSelectedFilingId(null);
          setSelectedRequestId(null);
          setDetailsSourceGroup(null);
        }}
        onBack={
          detailsSourceGroup
            ? () => {
                setSelectedFilingId(null);
                setSelectedGroup(detailsSourceGroup);
                setDetailsSourceGroup(null);
              }
            : undefined
        }
      />
    </Box>
  );
};
